import { toast } from "@-/com.npmjs.sonner";
import { showErrorDetails } from "@-/dev.improve.haniverse.components.ui.error-details-sheet";
import ensureError from "@-/com.npmjs.ensure-error";
import {
  describeFailureForUser,
  isStudyDataUnavailableError,
} from "@-/dev.improve.haniverse.lib.error-classification";
import { getRuntimeScopedState } from "@-/dev.improve.haniverse.lib.runtime-scoped-state";

/**
 * The app's one "that didn't work" toast, shared by page navigation, failed writes and the Learn
 * summary so a failure reads the same wherever it surfaces (see `describeFailureForUser`).
 *
 * Study-data failures get ONE toast id: every tap that needs the unavailable database lands on the
 * same notice instead of stacking a new one, and it offers support rather than error details.
 */
const STUDY_DATA_UNAVAILABLE_TOAST_ID = "study-data-unavailable";

// Runtime-scoped: the handler closes over a live navigator owned by a mounted component.
const getContactSupportSlot = () =>
  getRuntimeScopedState(
    "components.ui.failure-toast.contact-support",
    (): { current: (() => void) | null } => ({ current: null }),
  );

/**
 * Register how to open support. Page navigation is component-scoped (direct router calls are
 * banned), so the app shell registers its support navigator here while it is mounted.
 */
export function registerContactSupport(handler: () => void): () => void {
  const slot = getContactSupportSlot();
  slot.current = handler;
  return () => {
    if (slot.current === handler) {
      slot.current = null;
    }
  };
}

/** Opens support if the app shell has registered a way to. Returns whether it did. */
export function contactSupport(): boolean {
  const handler = getContactSupportSlot().current;
  if (handler === null) {
    return false;
  }
  handler();
  return true;
}

export function showFailureToast(error: unknown): void {
  const failure = describeFailureForUser(error);
  const isStudyDataUnavailable = failure.kind === "study-data-unavailable";
  const toastId = toast.error(failure.title, {
    action:
      isStudyDataUnavailable && getContactSupportSlot().current !== null
        ? {
            label: "Contact Support",
            onClick: () => {
              contactSupport();
              toast.dismiss(toastId);
            },
          }
        : {
            label: "Show Error Details",
            onClick: () => {
              showErrorDetails(error);
              toast.dismiss(toastId);
            },
          },
    description: failure.body,
    duration: 15_000,
    ...(isStudyDataUnavailable ? { id: STUDY_DATA_UNAVAILABLE_TOAST_ID } : {}),
  });
}

/**
 * A failed action's toast: a short headline the caller names ("Could not create deck") and a
 * "Show Error Details" action. Raw error text (SQL, params, stack-shaped messages) is only inlined
 * in development builds; release builds keep it behind the action so users aren't overwhelmed.
 */
export function showErrorToast(title: string, error: unknown): void {
  if (isStudyDataUnavailableError(error)) {
    showFailureToast(error);
    return;
  }
  const toastId = toast.error(title, {
    action: {
      label: "Show Error Details",
      onClick: () => {
        showErrorDetails(error);
        toast.dismiss(toastId);
      },
    },
    ...(__DEV__ ? { description: ensureError(error).message } : {}),
    duration: 15_000,
  });
}
